import { useRef, useState, useCallback, useEffect } from 'react';

export function useScrollIndicator({ isEnabled = true } = {}) {
  const containerNodeRef = useRef(null);
  const [indicator, setIndicator] = useState({ show: false, width: 0, left: 0 });
  const [container, setContainer] = useState(null);

  const containerRef = useCallback((node) => {
    containerNodeRef.current = node || null;
    setContainer(node || null);
  }, []);

  const update = useCallback(() => {
    const node = containerNodeRef.current;
    if (!node) return;

    const { scrollWidth, clientWidth, scrollLeft } = node;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 1) {
      setIndicator((prev) => (prev.show ? { show: false, width: 0, left: 0 } : prev));
      return;
    }

    const rawWidthPercent = (clientWidth / scrollWidth) * 100;
    const adjustedWidth = rawWidthPercent - 5;
    const widthPercent = Math.max(8, Math.min(adjustedWidth, 80));
    const availableTrack = Math.max(0, 100 - widthPercent);
    const leftPercent = maxScroll > 0 ? (scrollLeft / maxScroll) * availableTrack : 0;

    setIndicator({ show: true, width: widthPercent, left: leftPercent });
  }, []);

  useEffect(() => {
    if (!isEnabled || !container) {
      setIndicator((prev) => (prev.show ? { show: false, width: 0, left: 0 } : prev));
      return;
    }

    const frame = requestAnimationFrame(() => update());

    const handleScroll = () => update();
    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', update);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => update());
      resizeObserver.observe(container);
    }

    return () => {
      cancelAnimationFrame(frame);
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', update);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [container, isEnabled, update]);

  return { containerRef, indicator, update };
}

export default useScrollIndicator;
